import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { v4 as uid } from 'uuid';
import tinymce from 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/autoresize';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/link';
import 'tinymce/plugins/autolink';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import { convertTextOnPaste } from './utils';

// import 'tinymce/skins/content/default/content.min.css';
// import 'tinymce/skins/ui/oxide/content.min.css';
// import 'tinymce/skins/ui/oxide/content.css';
// import 'tinymce/skins/ui/oxide/skin.min.css';
import TINYMCE_FIXED_STYLES from './tinymce_fixed_style';
import './index.scss';

interface ITinyEditorProps {
  customStyle?: React.CSSProperties;
  className?: string;
  isBackShow?: boolean; // 是否是回显模式
  isDefaultTriggerChange?: boolean; // 是否默认触发getEditorContent和getEditorText
  getEditorContent?: ((v: string) => void) | null; // 拿到当前富文本内容
  getEditorText?: ((v: string) => void) | null; // 拿到当前富文本纯文本内容
  getIsFocused?: (v: boolean) => void | null; // 判断当前编辑器是否聚焦
  status?: 'error' | 'success';
  errorTips?: string;
  defaultValue?: string;
  value?: string;
  placeholder?: string;
  autoFocus?: boolean;
  maxLength?: number;
  isShowToolbar?: boolean;
  readOnly?: boolean;
  isMaxHeightScroll?: boolean; // 超过最大高度出现滚动条
  maxContentHeight?: number; // 传了这个属性内容超出高度将出现滚动条
  // 是否监听外部传入的属性，同步更新最新的属性，默认不开启
  synchronize?: boolean;
  // 最小高度
  minHeight?: number;
}

const TinyEditor: React.FC<ITinyEditorProps> = (props) => {
  const {
    customStyle,
    className,
    isBackShow = false,
    isDefaultTriggerChange = true,
    getEditorContent: getEditorContentProps = null,
    getEditorText: getEditorTextProps = null,
    getIsFocused: getIsFocusedProps = null,
    status = 'success',
    errorTips = '',
    defaultValue = '',
    value = '',
    placeholder = '',
    autoFocus = false,
    isShowToolbar = true,
    readOnly = false,
    isMaxHeightScroll = true,
    maxContentHeight = 350,
    synchronize = false,
    minHeight = 200,
  } = props;

  const editorRef = useRef<any>(null);
  const getEditorContent = useRef<((v: string) => void) | null>(getEditorContentProps);
  const getEditorText = useRef<((v: string) => void) | null | undefined>(getEditorTextProps);
  const getIsFocused = useRef<((v: boolean) => void) | null | undefined>(getIsFocusedProps);
  const [isFocus, setIsFocus] = useState<boolean>(false);

  const uniqueId = useMemo(() => `dbg-tiny-editor${uid()}`, []);
  const initialValue = useMemo(() => defaultValue || value || '', [defaultValue, value]);
  getEditorContent.current = getEditorContentProps;
  getEditorText.current = getEditorTextProps;
  getIsFocused.current = getIsFocusedProps;

  useEffect(() => {
    tinymce.init({
      selector: `#${uniqueId}`,
      // relative_urls: true,
      convert_urls: false,
      // http://databrain-test.intlgame.com
      // ${window.location.origin}/globalpedia-pc
      // TODO: 后面要替换为新风格cdn的地址
      base_url: 'https://ogdb-cdn.intlgame.cn/front/pc/intelligence/intelligence_detail/tinymce', // 情报public目录下的tinymce
      // content_css: 'https://ogdb-cdn.intlgame.cn/front/pc/intelligence/intelligence_detail/tinymce/tinymce_fixed.css', // 富文本编辑区域的额外样式文件
      content_style: TINYMCE_FIXED_STYLES,
      plugins: 'lists autoresize wordcount link autolink',
      toolbar:
        !!isShowToolbar &&
        'bold | underline | strikethrough | bullist | numlist | align | clearStyle | undo | redo | link',
      contextmenu: 'openlink | unlink',
      statusbar: false, // 底部的状态栏
      menubar: false, // 最上方的菜单
      branding: false, // 水印“Powered by TinyMCE”
      resize: false,
      content: value,
      // highlight_on_focus: true,
      inline: false, // false：经典编辑模式；true: 行内编辑模式
      readonly: readOnly,
      placeholder,
      auto_focus: autoFocus || undefined,
      height: 'auto',
      min_height: minHeight,
      max_height: isMaxHeightScroll && maxContentHeight ? maxContentHeight : undefined,
      link_default_target: '_blank',
      link_default_protocol: 'http',
      link_context_toolbar: false,
      // 保留粘贴的格式
      /* paste_webkit_styles: `
        margin-right margin-left
        padding-right padding-left
        text-indent position right left
        text-align text-justify letter-spacing
      `, */
      paste_webkit_styles: 'all',
      paste_merge_formats: true, // 合并重复的样式
      paste_preprocess: (editor, args) => {
        try {
          args.content = convertTextOnPaste(args.content);
        } catch (exception) {
          console.warn('Tinymce convert on paste exception: ', exception);
        }
      },
      // paste_retain_style_properties: "all",
      // paste_word_valid_elements: "*[*]", // word需要它
      // paste_convert_word_fake_lists: true, // 插入word文档需要该属性
      // nonbreaking_force_tab: false,
      // paste_auto_cleanup_on_paste: false,
      // init_instance_callback(editor) {},
      setup(editor) {
        editorRef.current = editor;
        // 注册橡皮擦按钮
        editor.ui.registry.addIcon(
          'eraser',
          '<svg width="17" height="17" viewBox="0 0 1024 1024"><path d="M969.382408 288.738615l-319.401123-270.852152a67.074236 67.074236 0 0 0-96.459139 5.74922l-505.931379 574.922021a68.35184 68.35184 0 0 0-17.886463 47.910169 74.101061 74.101061 0 0 0 24.274486 47.910168l156.50655 132.232065h373.060512L975.131628 383.281347a67.074236 67.074236 0 0 0-5.74922-96.459139z m-440.134747 433.746725H264.144729l-90.071117-78.572676c-5.74922-5.74922-12.137243-12.137243-12.137243-17.886463a36.411728 36.411728 0 0 1 5.749221-24.274485l210.804741-240.828447 265.102932 228.691204z m-439.495945 180.781036h843.218964a60.047411 60.047411 0 1 1 0 120.733624H89.751716a60.047411 60.047411 0 1 1 0-120.733624z m0 0" fill-rule="evenodd"></path></svg>',
        );
        editor.ui.registry.addButton('clearStyle', {
          tooltip: 'Clear styles',
          icon: 'eraser',
          onAction: () => {
            editor.execCommand('removeformat', false, 'clearStyle');
          },
        });
        editor.on('init', () => {
          editor.setContent(initialValue);
          // 是否默认触发getEditorContent和getEditorText
          if (isDefaultTriggerChange) {
            const timer = setTimeout(() => {
              const text = editor.getContent({ format: 'text' }) || '';
              const isEmpty = !text?.trim();
              getEditorContent.current?.(isEmpty ? '' : editor.getContent());
              getEditorText.current?.(isEmpty ? '' : text);
              clearInterval(timer);
            }, 200);
          }

          // 往iframe内容体中添加固定的样式表
          // const baseUrl = `${window.location.origin}/globalpedia-pc`;
          // // const currentIframe = editor.getContentAreaContainer()?.getElementsByTagName('iframe')?.[0];
          // const cssLink = document.createElement('link');
          // cssLink.rel = 'stylesheet';
          // cssLink.type = 'text/css';
          // cssLink.href = `${baseUrl}/tinymce/tinymce_fixed.css`;
          /* || currentIframe?.contentDocument || currentIframe?.contentWindow?.document */
          const iframeDoc = editor?.getDoc();
          // iframeDoc?.getElementsByTagName('head')?.[0]?.appendChild(cssLink);

          // 设置文档区域hover指针为输入态形状
          if (iframeDoc?.documentElement) iframeDoc.documentElement.style.cursor = 'text';

          // 删除工具栏中按钮自带的title
          const editorContainer = editor?.getContainer();
          const toolbarEls = editorContainer?.querySelector(
            '.tox-editor-header .tox-toolbar__primary',
          )?.childNodes;
          const timer = setInterval(() => {
            if (toolbarEls?.length) {
              for (let i = 0, l = toolbarEls?.length;i < l;i++) {
                const t = toolbarEls?.[i];
                (t?.firstChild as Element)?.setAttribute('title', '');
              }
              clearInterval(timer);
            }
          }, 200);
        });
        // input时也触发change事件
        editor.on('input', () => {
          const text = editor.getContent({ format: 'text' }) || '';
          const isEmpty = !text?.trim();
          getEditorContent.current?.(isEmpty ? '' : editor.getContent());
          getEditorText.current?.(isEmpty ? '' : text);
        });
        editor.on('change', () => {
          const text = editor.getContent({ format: 'text' }) || '';
          const isEmpty = !text?.trim();
          getEditorContent.current?.(isEmpty ? '' : editor.getContent());
          getEditorText.current?.(isEmpty ? '' : text);
        });
        editor.on('focus', () => {
          setIsFocus(true);
          getIsFocused.current?.(true);
        });
        editor.on('blur', () => {
          setIsFocus(false);
          getIsFocused.current?.(false);
        });
        editor.on('keydown', (e) => {
          // 检查按键是否为Tab，如果是替换为缩进
          if (e.keyCode === 9) {
            e.preventDefault();
            e.stopPropagation();
            editor.execCommand('Indent');
          }
        });
      },
    });
  }, [
    uniqueId,
    initialValue,
    isShowToolbar,
    readOnly,
    placeholder,
    autoFocus,
    isMaxHeightScroll,
    maxContentHeight,
    isDefaultTriggerChange,
    minHeight,
  ]);

  useEffect(() => {
    if (!synchronize) return;
    if (!editorRef.current) return;
    editorRef.current.mode?.set(!!readOnly ? 'readonly' : 'design');
  }, [synchronize, readOnly]);

  // 及时销毁 editor ，重要！
  useEffect(
    () => () => {
      if (!editorRef?.current) return;
      editorRef.current?.destroy();
    },
    [],
  );

  return (
    <section
      style={customStyle}
      className={classnames(
        'dbg-tiny-editor-content',
        {
          'dbg-tiny-editor-showing': isBackShow,
          'dbg-tiny-editor-error-status': status === 'error',
          'dbg-blue-border': isFocus,
        },
        className,
      )}
    >
      <textarea id={uniqueId}></textarea>
      <label
        style={{ display: 'none' }}
        className={classnames({
          'dbg-tiny-editor-error-info': status === 'error',
        })}
      >
        {errorTips || 'Please check that the information is complete or correct.'}
      </label>
    </section>
  );
};

export default React.memo(TinyEditor);
